import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { useEffect } from 'react';
import { useLoaderData } from 'react-router';

import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CustomRowResults } from '@jane/bloom/loaders/routes/customRow.server';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';

import { CustomRowDetails } from './components/customRowDetails';

export function CustomRowRouteComponent() {
  const { cart, pinnedStore } = useRootLoaderData();
  const {
    availableFilters,
    brandSpecials,
    customLanguage,
    customRow,
    enabledSpecialsForRow,
    janeGold,
    jdid,
    menuProductImgsAboveTheFold,
    menuTopRow,
    recommendedSortResults,
    searchCriteria,
    searchFilterWithoutBrands,
    sort: { currentSort, sortOptions },
    storeId,
  } = useLoaderData<CustomRowResults>();

  useEffect(() => {
    registerJdidToRum(jdid);
  }, [jdid]);

  const rowInstance = useMemoizedRowInstance(recommendedSortResults);

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: menuTopRow,
    searchFilter: searchFilterWithoutBrands,
    storeId,
  });

  useEffect(() => {
    if (!rowInstance) return;
    trackSearchedMenuProducts({
      hits: rowInstance?.totalProducts,
      searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(searchCriteria), rowInstance?.totalProducts]);

  return (
    <BloomFilterProvider
      availableFilters={availableFilters}
      searchCriteria={searchCriteria}
    >
      <CustomRowDetails
        pauseRefetching={pauseRefetching}
        unpauseRefetching={unpauseRefetching}
        brandSpecials={brandSpecials}
        cart={cart}
        customLanguage={customLanguage}
        customRow={customRow}
        currentSort={currentSort}
        specials={enabledSpecialsForRow}
        menuTopRowInstance={menuTopRowInstance}
        preloadedProductIds={menuProductImgsAboveTheFold.map(({ id }) => id)}
        sortOptions={sortOptions}
        rowInstance={rowInstance}
        janeGold={janeGold}
        pinnedStore={pinnedStore}
      />
    </BloomFilterProvider>
  );
}
